import urlHelper from '../helpers/url'

export function isFolderNode(node) {
  return node.content !== undefined
}

export function isImageNode(node) {
  return node.image !== undefined
}

export function imageURL(image) {
  return image
    ? `https://firebasestorage.googleapis.com/v0/b/${window.contentServer}/o/images%2F${image}?alt=media`
    : ''
}

export function isEmptyContentNode(content, nodeId) {
  const node = nodeWithId(content, nodeId)
  if (!node) return false

  if (node.content !== undefined) return false

  const title = node.title ?? ''
  const text = node.text ?? ''
  const image = node.image ?? ''

  return title.length === 0 && text.length === 0 && image.length === 0
}

function isVideoNode(node) {
  if (node.text === undefined) {
    return null
  }
  const video = urlHelper.getVideoDetails(node.text)
  return video !== null
}

export function nodeType(node) {
  if (isFolderNode(node)) {
    return 'folder'
  }
  if (isImageNode(node)) {
    return 'image'
  }
  if (isVideoNode(node)) {
    return 'video'
  }
  return 'text'
}

function defaultIcon(isRoot) {
  return isRoot ? '⭐' : '📂'
}

export function defaultTitle(isRoot) {
  return isRoot ? 'My Board' : 'My Folder'
}

export function displayIcon(node, isRoot) {
  const icon = node.icon ?? ''
  return icon.length > 0 ? icon : defaultIcon(isRoot)
}

export function displayTitle(node, isRoot) {
  const title = node.title ?? ''
  return title.length > 0 ? title : defaultTitle(isRoot)
}

function withNodeReplaced(folder, newNode) {
  // immutably replaces a node in a folder tree
  if (folder.id === newNode.id) return newNode

  const { content } = folder
  let i = content.findIndex((node) => node.id === newNode.id)
  if (i >= 0)
    return {
      ...folder,
      content: [...content.slice(0, i), newNode, ...content.slice(i + 1)],
    }

  const { length } = content
  for (i = 0; i < length; i++) {
    const subfolder = content[i]
    if (subfolder.content !== undefined) {
      const newSubfolder = withNodeReplaced(subfolder, newNode)
      if (newSubfolder !== undefined) {
        return {
          ...folder,
          content: [
            ...content.slice(0, i),
            newSubfolder,
            ...content.slice(i + 1),
          ],
        }
      }
    }
  }
  return undefined
}

export function nodeWithId(node, id) {
  if (node.id === id) return node

  const { content } = node
  if (content !== undefined) {
    const { length } = content
    for (let i = 0; i < length; i++) {
      const found = nodeWithId(content[i], id)
      if (found !== null) {
        return found
      }
    }
  }
  return null
}

export function parentOfId(folder, id) {
  const { content } = folder
  if (content === undefined) {
    return null
  }
  const { length } = content
  for (let i = 0; i < length; i++) {
    const node = content[i]
    if (node.id === id) {
      return folder
    }
    const parent = parentOfId(node, id)
    if (parent !== null) {
      return parent
    }
  }
  return null
}

export function pathOfId(content, nodeId) {
  const path = []

  while (nodeId != null) {
    path.unshift(nodeId)
    nodeId = parentOfId(content, nodeId)?.id
  }

  return path
}

export function bestIdWithPath(content, path) {
  while (path.length > 0) {
    const nodeId = path.splice(-1, 1)[0]
    if (nodeWithId(content, nodeId) != null) {
      return nodeId
    }
  }
  return undefined
}

function withNodeAdded(content, parentId, node) {
  const parent = nodeWithId(content, parentId)
  if (parent === null) return content

  const newContent = withNodeReplaced(content, {
    ...parent,
    content: [...parent.content, { ...node, id: content.nextId }],
  })
  newContent.nextId += 1
  return newContent
}

function forEachDescendantFolder(folder, block) {
  folder.content.forEach((node) => {
    if (node.content !== undefined) {
      block(node)
      forEachDescendantFolder(node, block)
    }
  })
}

function usedEmojis(content) {
  const used = []
  forEachDescendantFolder(content, (node) => {
    if (node.icon !== undefined) {
      if (!used.includes(node.icon)) {
        used.push(node.icon)
      }
    }
  })
  return used
}

export function randomEmojiExcluding(excluding) {
  const emojis = [
    '😀',
    '💫',
    '⭐️',
    '🌟',
    '✨',
    '⚡️',
    '💬',
    '💭',
    '🗯',
    '🔱',
    '🌱',
    '🍀',
    '🎱',
    '🎨',
    '🎯',
    '🎋',
    '🥋',
    '🏆',
    '🏰',
    '📢',
    '💣',
    '🏁',
    '🚩',
    '🌀',
    '💡',
    '⚖️',
    '🧭',
    '🏖',
    '🍥',
    '🚀',
  ]

  const unused = emojis.filter((emoji) => !excluding.includes(emoji))

  if (unused.length > 0) {
    return unused[Math.floor(Math.random() * unused.length)]
  }

  return emojis[Math.floor(Math.random() * emojis.length)]
}

export function withFolderAdded(content, parentId) {
  const emoji = randomEmojiExcluding(usedEmojis(content))
  return withNodeAdded(content, parentId, { content: [], icon: emoji })
}

export function withTextNodeAdded(content, parentId) {
  return withNodeAdded(content, parentId, {})
}

export function withImageNodeAdded(content, parentId, image) {
  return withNodeAdded(content, parentId, { image })
}

function byMoving(array, oldIndex, newIndex) {
  if (oldIndex === newIndex) {
    return array
  }
  const copy = [...array]
  copy.splice(newIndex, 0, copy.splice(oldIndex, 1)[0])
  return copy
}

export function withNodeMoved(content, parentId, nodeId, newIndex) {
  const parent = nodeWithId(content, parentId)
  if (parent === null) return content

  const oldIndex = parent.content.findIndex((node) => node.id === nodeId)
  if (oldIndex < 0 || oldIndex === newIndex) {
    return content
  }

  if (newIndex >= content.length) {
    return content
  }

  return withNodeReplaced(content, {
    ...parent,
    content: byMoving(parent.content, oldIndex, newIndex),
  })
}

export function withNodeRemoved(content, parentId, nodeId) {
  const parent = nodeWithId(content, parentId)
  if (parent === null) return content

  return withNodeReplaced(content, {
    ...parent,
    content: parent.content.filter((node) => node.id !== nodeId),
  })
}

export function withNodeRemovedIfEmpty(content, nodeId) {
  if (!isEmptyContentNode(content, nodeId)) return content

  const parent = parentOfId(content, nodeId)
  if (!parent) return content

  return withNodeRemoved(content, parent.id, nodeId)
}

export function withTitleSet(content, nodeId, title) {
  const node = nodeWithId(content, nodeId)
  if (node === null) return content

  const titleChanged =
    title !== node.title && !(title.length === 0 && node.title === undefined)
  if (!titleChanged) return content

  return withNodeReplaced(content, {
    ...node,
    title,
  })
}

export function withIconSet(content, nodeId, icon) {
  const node = nodeWithId(content, nodeId)
  if (node === null) return content

  const changed =
    icon !== node.icon && !(icon.length === 0 && node.icon === undefined)
  if (!changed) return content

  return withNodeReplaced(content, {
    ...node,
    icon,
  })
}

export function withTextSet(content, nodeId, text) {
  const node = nodeWithId(content, nodeId)
  if (node === null) return content

  const textChanged =
    text !== node.text && !(text.length === 0 && node.text === undefined)
  if (!textChanged) return content

  return withNodeReplaced(content, {
    ...node,
    text,
  })
}

export function withImageSet(content, nodeId, image) {
  const node = nodeWithId(content, nodeId)
  if (node === null) return content

  if (node.image === image) return content

  return withNodeReplaced(content, {
    ...node,
    image,
  })
}

function strippedNode(node) {
  const n = { ...node }
  if (n.title !== undefined && n.title.length === 0) {
    delete n.title
  }
  if (n.text !== undefined && n.text.length === 0) {
    delete n.text
  }
  if (n.content !== undefined) {
    n.content = n.content.map((contentNode) => strippedNode(contentNode))
  }
  return n
}

export function stripped(content) {
  return strippedNode(content)
}

function addIds(root, content) {
  const { length } = content
  for (let i = 0; i < length; i++) {
    const node = content[i]
    // eslint-disable-next-line
    root.nextId += 1
    node.id = root.nextId
    if (node.content !== undefined) {
      addIds(root, node.content)
    }
  }
}

export function fixed(content) {
  const root = Array.isArray(content) ? { content } : content

  if (root.nextId === undefined) {
    root.id = 0
    root.nextId = 1
    addIds(root, root.content)
  }
  return root
}
